import React from 'react';
import { useTranslation } from 'react-i18next';

import { useCheckoutAccounting } from '../../../../store/configurator';
import { SPEI } from '../../spei.tokens';
import { Row, Labels, Label, Value, CopyButton } from './CopyField.styles';

export type CopyIconButtonProps = {
  /** What the button says it copies, e.g. "CLABE" — used for the accessible name. */
  label: string;
  /** The exact text written to the clipboard. */
  value: string;
  /** Analytics id suffix, e.g. `clabe` → `data-ga-id="cfg-mx-copy-clabe"`. */
  gaId: string;
};

export type CopyFieldProps = CopyIconButtonProps & {
  /**
   * What to DISPLAY, when it differs from what is copied — the CLABE is shown in spaced
   * groups for readability but must be copied as bare digits, since a banking app rejects
   * the spaces. Defaults to `value`.
   */
  display?: string;
};

/** How long the button stays in its "copied" (green tick) state. */
const COPIED_MS = 2000;

const CopyGlyph = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="9" y="9" width="11" height="11" rx="2.5" stroke={SPEI.copyIcon} strokeWidth="1.6" />
    <path
      d="M15 6.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h.5"
      stroke={SPEI.copyIcon}
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

const CopiedGlyph = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="m5 12.5 4.5 4.5L19 7.5"
      stroke={SPEI.copiedIcon}
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Write `text` to the clipboard. Prefers the async Clipboard API and falls back to a
 * throwaway `<textarea>` + `execCommand`, because the modern API is unavailable outside a
 * secure context (the LAN-IP phone-testing setup) — and a CLABE nobody can copy makes the
 * whole screen unusable. Resolves to whether the copy actually happened.
 */
async function writeToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* Blocked (permissions / non-secure context) — fall through to the legacy path. */
  }
  try {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    // Off-screen rather than `display:none`: a hidden element can't be selected.
    area.style.position = 'fixed';
    area.style.opacity = '0';
    area.style.pointerEvents = 'none';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

/**
 * The copy control itself: a square ghost button that flips to a green tick for a couple of
 * seconds so the buyer can see the copy landed.
 *
 * Copying is the primary interaction on the SPEI screen (these values get retyped into a
 * banking app, where a single wrong digit sends the money nowhere), so it is a real
 * `<button>` whose accessible name states both what it copies and that it did.
 */
export const CopyIconButton = ({ label, value, gaId }: CopyIconButtonProps): React.ReactElement => {
  const { t } = useTranslation();
  // Copies are accounted for TWICE, on purpose: `data-ga-id` feeds the analytics stream via
  // the configurator's auto-capture click tracking, and this records it onto the SESSION so
  // the funnel survives without analytics consent (and is queryable server-side).
  const recordCheckout = useCheckoutAccounting();
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<number | null>(null);

  // The "copied" flash is a timer we own; clear it if the button unmounts mid-flash.
  React.useEffect(
    () => () => {
      if (timer.current != null) window.clearTimeout(timer.current);
    },
    [],
  );

  const copy = React.useCallback(async (): Promise<void> => {
    const ok = await writeToClipboard(value);
    // Only count a copy that actually reached the clipboard — a blocked write is not a signal
    // that the buyer took the coordinate.
    if (!ok) return;
    recordCheckout({ copied: gaId });
    setCopied(true);
    if (timer.current != null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      setCopied(false);
    }, COPIED_MS);
  }, [value, gaId, recordCheckout]);

  return (
    <CopyButton
      type="button"
      $copied={copied}
      data-ga-id={`cfg-mx-copy-${gaId}`}
      onClick={() => void copy()}
      aria-label={copied ? t('{{label}} copied', { label }) : t('Copy {{label}}', { label })}
    >
      {copied ? CopiedGlyph : CopyGlyph}
    </CopyButton>
  );
};

/** One bank-coordinate row: caption, value, and its copy button. */
export const CopyField = ({ label, value, display, gaId }: CopyFieldProps): React.ReactElement => (
  <Row>
    <Labels>
      <Label text={label} tag="span" />
      <Value text={display ?? value} tag="span" />
    </Labels>
    <CopyIconButton label={label} value={value} gaId={gaId} />
  </Row>
);

export default CopyField;
